import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const Header = ({
  user,
  onLogout,
  onSearch,
  onStatusFilter,
  onTagFilter,
  onAddBookmark,
  onImport,
  onExport,
  onOpenSettings,
  onManageCategories,
  onManageTags,
  statuses,
  tags,
  selectedTag
}) => {
  const [searchValue, setSearchValue] = useState('');
  const { theme, toggleTheme, isDark } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    onSearch(e.target.value);
  };

  return (
    <header className="app-header px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-foreground hidden sm:block">{t('app_name')}</h1>
        </div>

        {/* Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
              placeholder={t('search')}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background text-sm"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 hover:bg-accent/10 rounded-lg transition-colors hidden sm:flex"
            title={isDark ? t('theme_light') : t('theme_dark')}
            data-testid="theme-toggle"
          >
            {isDark ? (
              <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          <button
            onClick={onAddBookmark}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium text-sm"
            data-testid="add-bookmark-button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">{t('new')}</span>
          </button>

          <button
            onClick={onImport}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-accent/10 transition-colors text-sm"
            data-testid="import-button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {t('import')}
          </button>

          <button
            onClick={onExport}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-accent/10 transition-colors text-sm"
            data-testid="export-button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            {t('export')}
          </button>

          {/* User Menu */}
          <div className="relative group">
            <button
              className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-accent/10 transition-colors"
              data-testid="user-menu-button"
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <svg className="w-4 h-4 text-muted-foreground hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown */}
            <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
              
              {/* Mobile-only buttons */}
              <button
                onClick={onImport}
                className="sm:hidden w-full px-4 py-2 text-left text-sm hover:bg-accent/10 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {t('import')}
              </button>
              <button
                onClick={onExport}
                className="sm:hidden w-full px-4 py-2 text-left text-sm hover:bg-accent/10 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                {t('export')}
              </button>
              
              {/* Theme toggle for mobile */}
              <button
                onClick={toggleTheme}
                className="sm:hidden w-full px-4 py-2 text-left text-sm hover:bg-accent/10 transition-colors flex items-center gap-2"
              >
                {isDark ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    {t('theme_light')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                    {t('theme_dark')}
                  </>
                )}
              </button>
              
              {/* Manage Categories */}
              <button
                onClick={onManageCategories}
                className="w-full px-4 py-2 text-left text-sm hover:bg-accent/10 transition-colors flex items-center gap-2 border-t border-border"
                data-testid="manage-categories-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                {t('manage_categories')}
              </button>
              
              {/* Manage Tags */}
              <button
                onClick={onManageTags}
                className="w-full px-4 py-2 text-left text-sm hover:bg-accent/10 transition-colors flex items-center gap-2"
                data-testid="manage-tags-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                {t('manage_tags')}
              </button>
              
              {/* Admin Panel - Only for admins */}
              {user.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-accent/10 transition-colors flex items-center gap-2 text-amber-600 dark:text-amber-400"
                  data-testid="admin-panel-button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Painel Admin
                </button>
              )}
              
              <button
                onClick={onOpenSettings}
                className="w-full px-4 py-2 text-left text-sm hover:bg-accent/10 transition-colors flex items-center gap-2 border-t border-border"
                data-testid="settings-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t('settings')}
              </button>
              <button
                onClick={onLogout}
                className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-accent/10 transition-colors flex items-center gap-2 border-t border-border"
                data-testid="logout-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('sign_out')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section - Status and Tags */}
      <div className="hidden sm:flex items-center gap-2 mt-3 overflow-x-auto pb-1">
        {/* Status filters */}
        <span className="text-sm text-muted-foreground">{t('filter')}</span>
        <button
          onClick={() => onStatusFilter(null)}
          className="px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap bg-secondary text-secondary-foreground hover:bg-secondary/80"
          data-testid="filter-all"
        >
          {t('all')}
        </button>
        {statuses.filter(s => s.is_default).map(status => (
          <button
            key={status.status_id}
            onClick={() => onStatusFilter(status.status_id)}
            className="px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
            style={{
              backgroundColor: status.color + '20',
              color: status.color
            }}
            data-testid={`filter-${status.status_id}`}
          >
            {status.name}
          </button>
        ))}
        
        {/* Divider */}
        {tags && tags.length > 0 && (
          <>
            <div className="w-px h-5 bg-border mx-1" />
            <span className="text-sm text-muted-foreground">{t('tags')}:</span>
            <button
              onClick={() => onTagFilter?.(null)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                !selectedTag ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
              data-testid="filter-tag-all"
            >
              {t('all')}
            </button>
            {tags.slice(0, 5).map(tag => (
              <button
                key={tag.tag_id}
                onClick={() => onTagFilter?.(tag.name)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                  selectedTag === tag.name ? 'ring-2 ring-offset-1' : ''
                }`}
                style={{
                  backgroundColor: tag.color + '20',
                  color: tag.color
                }}
                data-testid={`filter-tag-${tag.tag_id}`}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                {tag.name}
              </button>
            ))}
            {tags.length > 5 && (
              <span className="text-xs text-muted-foreground">+{tags.length - 5}</span>
            )}
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
